import {
	useState,
	useCallback,
	useEffect,
	useContext
} from 'react';
import classNames from 'classnames';

import { LayoutContext } from '../LayoutContext';

import Banner from '../../components/Banner';
import SearchFlyout from '../../components/SearchUnbxd/SearchFlyout';
import CartButton from '../../components/CartButton';
import AccountButton from '../../components/AccountButton';
import PlainButton from '../../components/PlainButton';
import Link from '../../components/Link';
import NavMenu from '../../components/NavMenu';
import NavMobileMenu from '../../components/NavMobileMenu';

import renderIcon from '../../utils/renderIcon';
import { useWindowEventListener } from '../../utils/hooks';
import useHeaderQuery from './useHeaderQuery';
import PrideLogo from '../../assets/images/logo/svg/BB_pride_logo.svg';

const ns = `site-header`;

const Header = ({pathname}) => {
	const {
		wp: {
			acfOptionsHeader: {
				acfHeaderSettings: {
					header: {
						navigationItems,
						navigationText,
						banner,
					},
				},
			},
		},
	} = useHeaderQuery();

	const { theme } = useContext(LayoutContext);

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [searchFlyoutOpen, setSearchFlyoutOpen] = useState(false);
	const [windowScrolled, setWindowScrolled] = useState(false);
	const isRainbow = false;

	const openMenuHandler = () => {
		setMobileMenuOpen(true);
		document.body.classList.add('modal-open');
	};

	const closeMenuHandler = () => {
		setMobileMenuOpen(false);
		document.body.classList.remove('modal-open');
	};

	const openSearchFlyoutHandler = () => {
		setSearchFlyoutOpen(true);
		document.body.classList.add('modal-open');
	};

	const closeSearchFlyoutHandler = () => {
		setSearchFlyoutOpen(false);
		document.body.classList.remove('modal-open');
	};

	useEffect(() => {
		setWindowScrolled(!!window.scrollY);
	}, []);

	useWindowEventListener('scroll', useCallback(() => {
		const { scrollY } = window;
		if (scrollY > 0) {
			setWindowScrolled(true);
		} else {
			setWindowScrolled(false);
		}
	}, []), { passive: true });

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--scroll`]: windowScrolled,
		[`${ ns }--search-flyout`]: searchFlyoutOpen,
		[`${ ns }--${ theme }`]: theme,
		[`${ ns }--rainbow`]: isRainbow,
	});

	const menuButtonClassnames = classNames({
		[`${ ns }__menu-button`]: true,
		[`${ ns }__menu-button--close`]: mobileMenuOpen,
	});

	return (
		<>
			{
				!!banner?.items?.length
				&& (
					<Banner items={banner.items} />
				)
			}
			<header className={`${ rootClassnames } ${ ns }__container`}>
				<div className={`container-fluid`}>
					<SearchFlyout
						open={searchFlyoutOpen}
						onCloseCallback={closeSearchFlyoutHandler}
						windowScrolled={windowScrolled}
					/>
					<div className={`${ ns }__block ${ ns }__block--left`}>
						<Link to={`/`} aria-label={`Go home`}>
							{isRainbow ? <PrideLogo /> : renderIcon('logo')}
						</Link>
					</div>
					<div className={`${ ns }__block ${ ns }__block--center`}>
						{
							navigationItems
							&& (
								<NavMenu
									menuItems={navigationItems}
									navigationText={navigationText}
									currentPathname={pathname}
								/>
							)
						}
					</div>
					<div className={`${ ns }__block ${ ns }__block--right`}>
						<div className={`${ ns }__account`}>
							<AccountButton />
						</div>
						<div className={`${ ns }__search`}>
							<button
								className={`${ ns }__search-flyout-trigger underline`}
								onClick={openSearchFlyoutHandler}
							>
								<span>Search</span>
								<span>
									{renderIcon('search')}
								</span>
							</button>
						</div>
						<div className={`${ ns }__cart`}>
							<CartButton />
						</div>
						<div className={`${ ns }__mobile-container`}>
							<PlainButton
								className={menuButtonClassnames}
								onClick={
									() => {
										if (mobileMenuOpen) {
											closeMenuHandler();
										} else {
											openMenuHandler();
										}
									}
								}
								aria-label={`Toggle mobile menu`}
							>
								{renderIcon('hamburger')}
							</PlainButton>
						</div>
					</div>
				</div>
				<NavMobileMenu
					menuItems={navigationItems}
					navigationText={navigationText}
					open={mobileMenuOpen}
					onCloseCallback={closeMenuHandler}
					collapseMenuOnClose={false} // Set to true if accordion items should collapse onClose.
					currentPathname={pathname}
				/>
			</header>
		</>
	);
};

export default Header;
